import React, { Fragment } from 'react';

import { saveEntity, deleteEntity } from '../actions/entity';
import { useSelector } from 'react-redux';

import { Link, useNavigate, useParams } from 'react-router-dom';
import Alert from 'react-s-alert';
import Error from './Error.jsx';
import Loading from '../components/Loading';
import UserSection from '../components/user/UserSection.jsx';
import Checkbox from '../components/form/FormikCheckbox.jsx';
import Select from '../components/form/FormikSelect.jsx';
import { useOrg } from './OrgWrapper';
import { useFormik } from 'formik';
import { userPermissions } from './UserDashboard';

const OrgView = () => {
    const params = useParams();
    const navigate = useNavigate();
    const session = useSelector((state) => state.session);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const { org, members, inProgress, isError, setOrg, setIsError } = useOrg(params.netsuite_entity);
    let isReadOnly = (session?.permissions || []).includes(userPermissions.READONLY);

    const handleSave = async (values) => {
        try {
            const resp = await saveEntity(params.netsuite_entity, values);
            Alert.success('Org Updated.');
            setOrg(resp.data);
            setIsError(false);
        } catch (error) {
            Alert.error('Error Saving.');
            Alert.error(error.message);
        }
    };

    const handleDelete = async () => {
        if (window.confirm(`Are you sure you want to remove ${org.name.long}?`)) {
            setIsDeleting(true);

            await deleteEntity(params.netsuite_entity);
            if (window.history.length === 1) {
                navigate('/');
            } else {
                navigate(-1);
            }
        }
    };

    const { values, errors, touched, isSubmitting, handleChange, handleSubmit } = useFormik({
        initialValues: {
            currency: org.currency || '',
            pif_enabled: org.pif_enabled || false,
            ils_enabled: org.ils_enabled || false,
            feature_split_by_allocations: org.feature_split_by_allocations || false,
            cc_only: org.cc_only || false
        },
        enableReinitialize: true,
        onSubmit: handleSave
    });

    if (isError) {
        return <Error />;
    }
    if (inProgress) {
        return <Loading />;
    }
    if (!org) {
        return <div>Org could not be loaded. Try again, or verify you have the correct Org NetSuite ID.</div>;
    }

    return (
        <Fragment>
            {isReadOnly && (
                <UserSection header="Information">
                    <ul className="list-flat spaced-single">
                        <li>
                            <strong>NetSuite ID:</strong>{' '}
                            <a
                                href={`https://system.na1.netsuite.com/app/common/entity/custjob.nl?id=${org.entity_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {org.entity_id}
                            </a>
                        </li>
                    </ul>
                    <ul className="list-flat">
                        <li>
                            <strong>Name:</strong> {org.name.long}
                        </li>
                        <li>
                            <strong>Currency:</strong> {org.currency}
                        </li>
                        <li>
                            <strong>Split By Allocations?</strong> {org.feature_split_by_allocations ? 'Yes' : 'No'}
                        </li>
                        <li>
                            <strong>PIF Enabled?</strong> {org.pif_enabled ? 'Yes' : 'No'}
                        </li>
                        <li>
                            <strong>ILS Enabled?</strong> {org.ils_enabled ? 'Yes' : 'No'}
                        </li>
                        <li>
                            <strong>Credit Card Payment Only?</strong> {org.cc_only ? 'Yes' : 'No'}
                        </li>
                    </ul>
                </UserSection>
            )}
            {!isReadOnly && (
                <UserSection header="Information">
                    <div className="mb-3">
                        <strong>NetSuite ID:</strong>{' '}
                        <a
                            href={`https://system.na1.netsuite.com/app/common/entity/custjob.nl?id=${org.entity_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {org.entity_id}
                        </a>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <Select
                            id="currency"
                            label="Currency"
                            value={values.currency}
                            error={errors.currency}
                            touched={touched.currency}
                            onChange={handleChange}
                        >
                            <option value="">Select Currency</option>
                            <option value="USD">US Dollar</option>
                            <option value="CAD">Canada Dollar</option>
                        </Select>

                        <Checkbox
                            label="Split Orders by ILS Allocations"
                            id="feature_split_by_allocations"
                            checked={values.feature_split_by_allocations}
                            error={errors.feature_split_by_allocations}
                            touched={touched.feature_split_by_allocations}
                            onChange={handleChange}
                            className="mb-2"
                        />

                        <Checkbox
                            label="PIF Enabled"
                            id="pif_enabled"
                            checked={values.pif_enabled}
                            error={errors.pif_enabled}
                            touched={touched.pif_enabled}
                            onChange={handleChange}
                            className="mb-2"
                        />

                        <Checkbox
                            label="ILS Enabled"
                            id="ils_enabled"
                            checked={values.ils_enabled}
                            error={errors.ils_enabled}
                            touched={touched.ils_enabled}
                            onChange={handleChange}
                            className="mb-2"
                        />

                        <Checkbox
                            label="Credit Card Payment Only"
                            id="cc_only"
                            checked={values.cc_only}
                            error={errors.cc_only}
                            touched={touched.cc_only}
                            onChange={handleChange}
                            className="mb-2"
                        />

                        <div className="mt-4">
                            <button className="button" type="submit" disabled={isSubmitting || isDeleting}>
                                {isSubmitting ? <Loading inline /> : 'Save'}
                            </button>{' '}
                            <button
                                className="button button-danger"
                                type="button"
                                onClick={handleDelete}
                                disabled={isSubmitting || isDeleting}
                            >
                                {isDeleting ? <Loading inline /> : 'Remove Org'}
                            </button>
                        </div>
                    </form>
                </UserSection>
            )}

            <UserSection header="Discounts">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Format</th>
                            <th>Discount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(org.price_contracts).map((discount) => {
                            return (
                                <tr key={discount}>
                                    <td>{discount}</td>
                                    <td>{org.price_contracts[discount] * 100}%</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </UserSection>

            <UserSection header="Service Kits">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>NetSuite ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!org.service_kits || !org.service_kits.length ? (
                            <tr>
                                <td colSpan={2}>
                                    <strong>NO Service Kits</strong>
                                </td>
                            </tr>
                        ) : (
                            org.service_kits.map((kit) => {
                                return (
                                    <tr key={kit.internalid}>
                                        <td>{kit.name}</td>
                                        <td>{kit.internalid}</td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </UserSection>

            <UserSection header="Org Members">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Last Login</th>
                        </tr>
                    </thead>
                    <tbody>
                        {members.map((member) => {
                            let _name = member.name ? `${member.name.given} ${member.name.family}` : '';
                            let name = _name.trim()
                                ? _name
                                : `No Name (${member._id.substring(member._id.length - 4)})`;
                            return (
                                <tr key={member._id}>
                                    <td>
                                        <Link to={`/customer/${member._id}`}>{name}</Link>
                                    </td>
                                    <td>{member.email}</td>
                                    <td>
                                        {member.last_login
                                            ? new Date(member.last_login).toLocaleDateString('en-US')
                                            : 'unknown'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </UserSection>
        </Fragment>
    );
};
export default OrgView;
